// WebAudio: synth SFX + cached voice clips + music bed.
// iOS unlock + node hygiene patterns follow pallasite/neon-sentinel.

import type { AnimalSpecies } from './animals';
import type { BiomeId } from './world';

const SETTINGS_KEY = 'takemetoyourledger:audio:v1';

export type GameAudioLevel = 0.75 | 1 | 1.25;

export interface AudioMixState {
  muted: boolean;
  music: boolean;
  game: GameAudioLevel;
}

const GAME_BUS_LEVEL = 1.25;

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let sfxBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let reverbSend: GainNode | null = null;
let unlocked = false;

function closestGameLevel(value: unknown): GameAudioLevel {
  return value === 0.75 || value === 1.25 ? value : 1;
}

const settings: AudioMixState = (() => {
  let parsed: Partial<AudioMixState> = {};
  try {
    parsed = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<AudioMixState>;
  } catch { /* ignore */ }
  return { muted: parsed?.muted === true, music: parsed?.music !== false, game: closestGameLevel(parsed?.game) };
})();

function persist(): void {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
}

export function isMuted(): boolean {
  return settings.muted;
}

export function audioMixState(): Readonly<AudioMixState> {
  return settings;
}

export function setAudioMix(next: AudioMixState): void {
  settings.muted = next.muted;
  settings.music = next.music;
  settings.game = next.game;
  persist();
  if (masterGain && ctx) masterGain.gain.setTargetAtTime(settings.muted ? 0 : 1, ctx.currentTime, 0.02);
  if (sfxBus && ctx) sfxBus.gain.setTargetAtTime(GAME_BUS_LEVEL * settings.game, ctx.currentTime, 0.03);
  for (const el of musicEls.values()) {
    el.muted = settings.muted || !settings.music || musicSuppressed;
    if (!settings.music) el.pause();
  }
  // Muting stops the standing beds outright rather than paying for oscillators
  // nobody can hear; they rebuild on the next frame that asks for them.
  if (settings.muted) {
    setBeamHum(false);
    setBlackHoleHum(false);
    stopRainLoop();
    stopRotorField();
    setHeliRotor(false);
    setGridHum(0);
  } else if (settings.music) nudgeMusic();
}

function getCtx(): AudioContext {
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  ctx = new Ctor();
  const compressor = ctx.createDynamicsCompressor();
  // Touch browsers play the music element outside this graph. The previous
  // broadcast-style squash therefore attenuated only SFX on mobile, placing
  // them behind an untouched bed. Keep peak safety without burying reactions.
  compressor.threshold.value = -10;
  compressor.ratio.value = 3;
  compressor.connect(ctx.destination);
  masterGain = ctx.createGain();
  masterGain.gain.value = settings.muted ? 0 : 1;
  masterGain.connect(compressor);
  sfxBus = ctx.createGain();
  sfxBus.gain.value = GAME_BUS_LEVEL * settings.game;
  sfxBus.connect(masterGain);
  musicBus = ctx.createGain();
  // Element volume owns the authored bed level. Keeping this bus at unity
  // makes desktop (routed through WebAudio) match mobile (direct element);
  // the old .55 here attenuated desktop music twice until it felt absent.
  musicBus.gain.value = 1;
  musicBus.connect(masterGain);
  // Valley air: a light convolution reverb send off the SFX bus. The impulse
  // is generated (decaying stereo noise), so it costs zero assets.
  try {
    const convolver = ctx.createConvolver();
    const dur = 1.8;
    const len = Math.floor(ctx.sampleRate * dur);
    const ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
      }
    }
    convolver.buffer = ir;
    reverbSend = ctx.createGain();
    reverbSend.gain.value = 0.16;
    sfxBus.connect(reverbSend);
    reverbSend.connect(convolver);
    convolver.connect(masterGain);
  } catch { /* reverb is a garnish; dry is fine */ }
  ctx.onstatechange = () => {
    if (ctx && ctx.state !== 'running' && unlocked) void ctx.resume().catch(() => undefined);
  };
  return ctx;
}

/** Call from inside a user gesture. Safe to call repeatedly. */
export function unlockAudio(): void {
  const c = getCtx();
  void c.resume().catch(() => undefined);
  if (!unlocked) {
    // 1-sample silent buffer forces iOS to actually open the audio session.
    const buf = c.createBuffer(1, 1, 22050);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start(0);
    unlocked = true;
  }
}

/** Let a deferred world feature build a long-lived sound inside the shared
 * SFX graph. Keeping ownership here preserves the game's mute/master chain. */
export function sfxGraph(): [AudioContext, GainNode] | null {
  if (settings.muted) return null;
  const c = getCtx();
  return sfxBus ? [c, sfxBus] : null;
}

/* ---------------- synth SFX ---------------- */

function env(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

/** Terminal node for a positioned sound: a stereo panner into the SFX bus,
 *  or the bus itself where StereoPannerNode is unsupported (old iOS). */
function panInto(pan: number): AudioNode {
  if (!ctx || !sfxBus) throw new Error('no ctx');
  if (pan === 0 || typeof ctx.createStereoPanner !== 'function') return sfxBus;
  const p = ctx.createStereoPanner();
  p.pan.value = Math.max(-1, Math.min(1, pan));
  p.connect(sfxBus);
  return p;
}

function tone(freqFrom: number, freqTo: number, dur: number, type: OscillatorType, peak: number, delay = 0, pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqFrom, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(freqTo, 1), t + dur);
  env(g, t, peak, 0.008, dur);
  const out = panInto(pan);
  osc.connect(g).connect(out);
  osc.start(t);
  osc.stop(t + dur + 0.05);
  osc.onended = () => { osc.disconnect(); g.disconnect(); if (out !== sfxBus) out.disconnect(); };
}

function noiseBurst(dur: number, peak: number, lowpass: number, pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) return;
  const t = ctx.currentTime;
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = lowpass;
  const g = ctx.createGain();
  g.gain.value = peak;
  const out = panInto(pan);
  src.connect(filter).connect(g).connect(out);
  src.start(t);
  src.onended = () => { src.disconnect(); filter.disconnect(); g.disconnect(); if (out !== sfxBus) out.disconnect(); };
}

export function sfxAbductPop(): void {
  tone(300, 900, 0.18, 'sine', 0.5);
  tone(600, 1800, 0.22, 'triangle', 0.25, 0.03);
}

/**
 * A moo with no recording behind it.
 *
 * This only plays when the real clip has not arrived yet — a cold cache, a
 * dropped connection — but a player on a bad train does not know that, so it
 * is built to the same standard as the rest of the farm rather than left as
 * the fallback nobody listens to. The formants are taken off the recording it
 * stands in for: a cow is a long tract, so its mouth resonances sit lower than
 * a goat's despite the animal being ten times the size.
 */
export function sfxCowMoo(pitch = 1, gain = 1, pan = 0): void {
  const voice = pitch * individual(0.05);
  const mouth = individual(0.05);
  voiceCall({
    dur: (1.25 / Math.sqrt(pitch)) * individual(0.08),
    pitch: [[0, 152 * voice], [0.18, 134 * voice], [0.7, 120 * voice], [1, 94 * voice]],
    amp: [[0.09, 0.9], [0.28, 1], [0.72, 0.88]],
    vowel: [
      [0, [300 * mouth, 760 * mouth, 1250 * mouth]],
      [0.22, [520 * mouth, 950 * mouth, 1400 * mouth]],
      [0.75, [500 * mouth, 900 * mouth, 1350 * mouth]],
      [1, [350 * mouth, 780 * mouth, 1250 * mouth]],
    ],
    q: [3, 3.5, 4],
    boost: [16, 14, 12],
    cutoff: 4400,
    air: 8,
    press: 0.84,
    jitter: 0.018,
    shimmer: 0.1,
    doubling: 0.28,
    chaos: [[0.85, 1, 0.3]],
    aspiration: 0.14,
    vibrato: [5.2, 4.2, 4 * voice, 8 * voice],
    tremolo: [5.5, 4, 0.16],
    rasp: 0.3,
    gain: gain * 1.5,
    pan,
  });
}

/** Real recorded moo (royalty-free) with pitch variation; synth fallback. */
export function playMoo(rate = 1, gain = 1, pan = 0): void {
  const buf = clipCache.get(VOICE.moo);
  if (buf && buf !== 'loading') {
    playVoiceClip(VOICE.moo, gain, rate, pan);
  } else {
    preloadVoiceClip(VOICE.moo);
    sfxCowMoo(rate, gain, pan);
  }
}

/* ---------------- animal voices ---------------- */

/**
 * One larynx for the whole farm.
 *
 * The cow is a real recording. Everything else is built the way the animal
 * builds it: a larynx makes a train of pressure pulses, a throat and a mouth
 * resonate it, and the lips radiate the result. What separates a real call
 * from a synthesised one is almost never the pitch or the formants — it is
 * that a real larynx is a floppy piece of tissue that does not repeat itself.
 * Bioacoustics has names for the ways it misbehaves, and every one of them is
 * available here because between them they are the sound of an animal:
 *
 *   jitter       cycle-to-cycle wobble in period — every mammal has some
 *   shimmer      cycle-to-cycle wobble in loudness
 *   doubling     the folds alternate long-short and drop an octave underneath
 *   biphonation  left and right folds run at unrelated rates at once
 *   chaos        the folds stop being periodic at all — the rasp in a bray
 *   jumps        the voice breaks into another register mid-call
 *
 * None of these can be built out of an oscillator and an LFO, which is why the
 * earlier attempt at these calls sounded synthetic however carefully its
 * formants were placed. So the source is generated a sample at a time into a
 * buffer, pulse by pulse, and only the resonances are left to the filter graph.
 */
interface VoiceSpec {
  /** Seconds of voiced sound. */
  dur: number;
  /** Voice pitch as [fraction of dur, Hz]; the first entry must sit at 0. */
  pitch: Array<[number, number]>;
  /** Loudness as [fraction of dur, 0..1]. The tail to silence is added here. */
  amp: Array<[number, number]>;
  /** The vocal tract: [fraction of dur, [F1, F2, F3] Hz]. */
  vowel: Array<[number, [number, number, number]]>;
  /** Formant sharpness — low is an open throat, high is a pinched nose. */
  q?: [number, number, number];
  /** How hard each formant is pushed, in dB. */
  boost?: [number, number, number];
  /** Where the mouth stops radiating, in Hz. Low is a muffled animal. */
  cutoff?: number;
  /** Presence lift in dB above 1.5 kHz — the band an ear uses to judge how
   *  close something is, and the one a muffled call has nothing in. */
  air?: number;

  /* ---- the larynx ---- */
  /** 0 is a soft, breathy, wide-open fold; 1 is a hard pressed one that snaps
   *  shut. This sets the pulse shape, and with it most of the brightness. */
  press?: number;
  /** Cycle-to-cycle period wobble as a fraction. 0.01 is a healthy animal,
   *  0.05 a strained one, past 0.1 an ill one. */
  jitter?: number;
  /** Cycle-to-cycle loudness wobble. */
  shimmer?: number;
  /** Period doubling: every other pulse is weaker, which puts an octave-down
   *  subharmonic under the voice. This is the single most common nonlinearity
   *  in mammal calls and the reason animals sound rough and machines do not. */
  doubling?: number;
  /** A second set of folds running at an unrelated rate, as a ratio of f0.
   *  Two frequencies with no common factor beat against each other: the
   *  strangled quality in a goat and a donkey. */
  biphonation?: number;
  /** Windows of deterministic chaos as [from, to, amount] in fractions of
   *  dur. In chaos the period and amplitude stop being predictable at all. */
  chaos?: Array<[number, number, number]>;
  /** Register breaks as [fraction, f0 multiplier] — the voice cracks. */
  jumps?: Array<[number, number]>;
  /** Turbulent air at the glottis, loudest just after each closure. */
  aspiration?: number;
  /** Amplitude modulation [Hz at start, Hz at end, depth 0..1]. */
  tremolo?: [number, number, number];
  /** 0 makes the tremolo a smooth swell, 1 a train of separate pulses. */
  tremoloShape?: number;
  /** Pitch vibrato [Hz start, Hz end, depth start (Hz), depth end (Hz)]. */
  vibrato?: [number, number, number, number];
  /** Overdrive after the larynx: the buzz in a bray or an oink. */
  rasp?: number;

  gain?: number;
  pan?: number;
  /** Seconds from now, so one call can be built from scheduled syllables. */
  at?: number;
}

/** Read a [fraction, value] contour at a fraction of the call. */
function atFraction(points: Array<[number, number]>, frac: number): number {
  if (points.length === 0) return 0;
  if (frac <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    if (frac <= points[i][0]) {
      const [f0, v0] = points[i - 1];
      const [f1, v1] = points[i];
      const span = f1 - f0;
      return span <= 0 ? v1 : v0 + (v1 - v0) * ((frac - f0) / span);
    }
  }
  return points[points.length - 1][1];
}

/**
 * The Rosenberg glottal pulse, differentiated.
 *
 * Airflow through the folds rises slowly as they open and is cut off sharply
 * as they slam shut, and it is that closure — not the opening — that excites
 * the vocal tract. Radiation from the mouth differentiates the flow, so what
 * actually leaves the animal is this derivative: a gentle rise and a hard
 * negative spike, once per cycle. A sawtooth is a crude imitation of the same
 * thing, which is why sawtooth animals sound like buzzers.
 *
 * `open` is the fraction of the cycle the folds spend apart: wide for a
 * relaxed animal, narrow and bright for one that is shouting.
 */
function glottalPulse(phase: number, open: number): number {
  const rise = open * 0.62;
  const fall = open - rise;
  const spike = Math.PI / (2 * fall);
  if (phase < rise) return (Math.PI / (2 * rise)) * Math.sin((Math.PI * phase) / rise);
  if (phase < open) return -spike * Math.sin((Math.PI / 2) * ((phase - rise) / fall));
  // The return phase. Folds have mass, so flow does not stop dead at closure —
  // it recovers over a short tail. Leaving that out puts a step discontinuity
  // once per cycle, and a step is a click: it was why the first version of
  // this came out as a bright electronic buzz rather than an animal.
  const ret = 0.1;
  if (phase < open + ret) return -spike * Math.exp((-(phase - open) / ret) * 3.5);
  return 0;
}

/**
 * Build one call's excitation, pulse by pulse. Everything that makes the voice
 * sound alive happens here; the filter graph downstream only decides which
 * animal's head it is coming out of.
 */
function glottalSource(c: AudioContext, spec: VoiceSpec): AudioBuffer {
  // Generated at 24 kHz and resampled on playback. Nothing in a throat
  // survives past the mouth cutoff a few kHz up, so the top half of the
  // context's bandwidth would be spent generating silence — and this loop runs
  // on the frame that fires the call, where a phone can feel it.
  const sr = Math.min(c.sampleRate, 24000);
  const length = Math.max(2, Math.ceil(sr * spec.dur));
  const buffer = c.createBuffer(1, length, sr);
  const data = buffer.getChannelData(0);

  const open = 0.78 - 0.42 * (spec.press ?? 0.5);
  const jitter = spec.jitter ?? 0;
  const shimmer = spec.shimmer ?? 0;
  const doubling = spec.doubling ?? 0;
  const aspiration = spec.aspiration ?? 0;
  const bi = spec.biphonation ?? 0;
  const tremShape = 1 + (spec.tremoloShape ?? 0) * 7;

  let phase = 0;
  let biPhase = 0;
  let cycle = 0;
  let periodScale = 1;
  let cycleGain = 1;
  let tremPhase = 0;
  let peak = 1e-6;
  // Aspiration is turbulence in a fleshy tube, not white noise: band it into
  // roughly 400 Hz - 3 kHz so breath reads as breath and not as tape hiss.
  const kLow = Math.min(1, (2 * Math.PI * 3000) / sr);
  const kHigh = Math.min(1, (2 * Math.PI * 400) / sr);
  let noiseLow = 0;
  let noiseHigh = 0;

  for (let i = 0; i < length; i++) {
    const frac = i / length;
    let f0 = atFraction(spec.pitch, frac);
    if (spec.vibrato) {
      const [rateFrom, rateTo, depthFrom, depthTo] = spec.vibrato;
      const rate = rateFrom + (rateTo - rateFrom) * frac;
      const depth = depthFrom + (depthTo - depthFrom) * frac;
      f0 += depth * Math.sin(2 * Math.PI * rate * (i / sr));
    }
    if (spec.jumps) {
      for (const [at, ratio] of spec.jumps) if (frac >= at) f0 *= ratio;
    }
    let chaos = 0;
    if (spec.chaos) {
      for (const [from, to, amount] of spec.chaos) {
        if (frac >= from && frac <= to) chaos = Math.max(chaos, amount);
      }
    }

    const step = (Math.max(20, f0) * periodScale) / sr;
    phase += step;
    if (phase >= 1) {
      phase -= 1;
      cycle++;
      // A new cycle is where the larynx gets to misbehave: it can run long or
      // short, loud or quiet, and in chaos it does both by a lot.
      periodScale = 1 + (Math.random() * 2 - 1) * (jitter + chaos * 0.45);
      cycleGain = 1 + (Math.random() * 2 - 1) * (shimmer + chaos * 0.5);
      if (doubling > 0 && cycle % 2 === 1) cycleGain *= 1 - doubling;
      cycleGain = Math.max(0.05, cycleGain);
    }

    let sample = glottalPulse(phase, open) * cycleGain;
    if (bi > 0) {
      // The second pair of folds. Deliberately not a harmonic of the first:
      // an integer ratio would just thicken the note instead of fighting it.
      biPhase += (Math.max(20, f0) * 1.407 * periodScale) / sr;
      if (biPhase >= 1) biPhase -= 1;
      sample += glottalPulse(biPhase, open) * bi * cycleGain;
    }
    if (aspiration > 0) {
      noiseLow += (Math.random() * 2 - 1 - noiseLow) * kLow;
      noiseHigh += (noiseLow - noiseHigh) * kHigh;
      // Turbulence is loudest through the open glottis and just after it slams.
      const gate = phase < open ? 1 : 0.35;
      sample += (noiseLow - noiseHigh) * 2.4 * aspiration * gate * (1 + chaos);
    }
    if (spec.tremolo) {
      const [from, to, depth] = spec.tremolo;
      tremPhase += (from + (to - from) * frac) / sr;
      const shaped = Math.pow(0.5 + 0.5 * Math.cos(2 * Math.PI * tremPhase), tremShape);
      sample *= 1 - depth + depth * shaped;
    }
    data[i] = sample;
    const size = Math.abs(sample);
    if (size > peak) peak = size;
  }
  // Normalised, so a spec can be designed for character and the loudness
  // contour alone decides how loud the animal is.
  for (let i = 0; i < length; i++) data[i] /= peak;
  return buffer;
}

/** Soft clip, for voices pushed harder than the throat can cleanly carry. */
const raspCurves = new Map<number, Float32Array<ArrayBuffer>>();
function raspCurve(drive: number): Float32Array<ArrayBuffer> {
  const key = Math.round(Math.max(0, Math.min(1, drive)) * 4) / 4;
  const cached = raspCurves.get(key);
  if (cached) return cached;
  const n = 1024;
  // Backed by an explicit ArrayBuffer: WaveShaper.curve will not take the
  // maybe-shared buffer type a bare `new Float32Array(n)` infers.
  const curve = new Float32Array(new ArrayBuffer(n * 4));
  const k = 1 + key * 16;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x) / Math.tanh(k);
  }
  raspCurves.set(key, curve);
  return curve;
}

function voiceCall(spec: VoiceSpec): void {
  if (settings.muted || !sfxBus || !ctx) { if (!ctx) getCtx(); }
  if (settings.muted || !sfxBus || !ctx) return;
  const c = ctx;
  const t = c.currentTime + (spec.at ?? 0);
  const dur = spec.dur;
  const nodes: AudioNode[] = [];
  const out = panInto(spec.pan ?? 0);

  const src = c.createBufferSource();
  src.buffer = glottalSource(c, spec);

  // The mouth: three resonances in series, and which three is the whole
  // difference between a goat and a pig.
  //
  // In series as peaking filters, not in parallel as bandpasses. Parallel
  // bandpasses keep only their own narrow slices and throw away everything
  // between and above them; a real throat colours the whole spectrum rather
  // than cutting holes in it, and the difference is audible as a call that
  // sounds like it is coming through a wall.
  const throat = c.createGain();
  const qs = spec.q ?? [4, 4.5, 5];
  const boosts = spec.boost ?? [15, 12, 7];
  let tract: AudioNode = throat;
  for (let i = 0; i < 3; i++) {
    const res = c.createBiquadFilter();
    res.type = 'peaking';
    res.Q.value = qs[i];
    res.gain.value = boosts[i];
    spec.vowel.forEach(([frac, formants], k) => {
      const hz = Math.max(60, formants[i]);
      if (k === 0) res.frequency.setValueAtTime(hz, t);
      else res.frequency.linearRampToValueAtTime(hz, t + frac * dur);
    });
    tract.connect(res);
    tract = res;
    nodes.push(res);
  }
  // Lips: a mouth radiates its top end better than its bottom, and stops
  // radiating altogether a few kHz up.
  const lips = c.createBiquadFilter();
  lips.type = 'highshelf';
  lips.frequency.value = 1500;
  lips.gain.value = spec.air ?? 3.5;
  const mouth = c.createBiquadFilter();
  mouth.type = 'lowpass';
  mouth.frequency.value = spec.cutoff ?? 5200;
  mouth.Q.value = 0.7;

  const env = c.createGain();
  const loudest = Math.max(0.02, 0.075 * (spec.gain ?? 1));
  env.gain.setValueAtTime(0.0001, t);
  for (const [frac, level] of spec.amp) {
    env.gain.exponentialRampToValueAtTime(Math.max(0.0004, level * loudest), t + frac * dur);
  }
  env.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  if (spec.rasp) {
    const shaper = c.createWaveShaper();
    shaper.curve = raspCurve(spec.rasp);
    shaper.oversample = '2x';
    src.connect(shaper).connect(throat);
    nodes.push(shaper);
  } else {
    src.connect(throat);
  }
  tract.connect(lips).connect(mouth).connect(env).connect(out);
  nodes.push(src, throat, lips, mouth, env);

  src.start(t);
  src.stop(t + dur + 0.05);
  src.onended = () => {
    for (const node of nodes) node.disconnect();
    if (out !== sfxBus) out.disconnect();
  };
}

/**
 * Every animal in a herd has its own throat and its own hurry. A call that
 * comes out identical twice is the tell that it came from a machine, so each
 * one is built with a slightly different mouth at a slightly different speed.
 */
function individual(spread: number): number {
  return 1 + (Math.random() * 2 - 1) * spread;
}

/**
 * Maaaah.
 *
 * A goat bleat is not a long note with a wobble on it. Measured, it is a
 * 0.3-0.7 s call around 300-450 Hz whose loudness is chopped at 20-30 Hz hard
 * enough that the individual pulses are audible, and it is one of the most
 * reliably nonlinear calls in farm bioacoustics: subharmonics and biphonation
 * turn up in most recordings, and the tail very often breaks into chaos. The
 * mouth opens from a closed "m" into a flat nasal "aeh" in the first fifth.
 *
 * The formants sit well above the sheep's on purpose. The two animals are the
 * same size and nearly the same shape, and it is the nose that tells them
 * apart — get this wrong and the moor's stray goats sound like small sheep.
 */
export function sfxGoatBleat(pitch = 1, gain = 1, pan = 0): void {
  const mouth = individual(0.07);
  const voice = pitch * individual(0.06);
  const rattle = 21 + Math.random() * 6;
  voiceCall({
    dur: (0.52 / Math.sqrt(pitch)) * individual(0.12),
    pitch: [[0, 330 * voice], [0.12, 424 * voice], [0.6, 392 * voice], [1, 302 * voice]],
    amp: [[0.03, 1], [0.42, 0.94], [0.8, 0.72]],
    vowel: [
      [0, [620 * mouth, 1700 * mouth, 3000 * mouth]],
      [0.2, [1000 * mouth, 2260 * mouth, 3200 * mouth]],
      [1, [900 * mouth, 2100 * mouth, 3050 * mouth]],
    ],
    q: [4.5, 5, 5.5],
    boost: [14, 14, 12],
    cutoff: 5400,
    air: 3,
    press: 0.82,
    jitter: 0.028,
    shimmer: 0.14,
    doubling: 0.34,
    biphonation: 0.16,
    chaos: [[0.78, 1, 0.5]],
    aspiration: 0.1,
    tremolo: [rattle, rattle + 8, 0.85],
    tremoloShape: 0.55,
    vibrato: [rattle, rattle + 8, 7 * voice, 13 * voice],
    rasp: 0.3,
    gain: gain * 1.15,
    pan,
  });
}

/**
 * A reindeer does not bellow, it croaks.
 *
 * Rutting males carry a descended larynx and an inflatable air sac, which puts
 * their formants lower than anything else on the farm, and the call itself is
 * a train of separate pulses rather than a tone — closer to a bullfrog than to
 * a cow. So the tremolo here runs at full depth with a hard pulse shape: what
 * you hear is meant to be the individual pulses, not a note being wobbled.
 * Heavy period doubling on top, because the folds of an animal that big rarely
 * manage to close cleanly twice in a row.
 */
export function sfxReindeerGrunt(pitch = 1, gain = 1, pan = 0): void {
  const grunts = 2 + (Math.random() < 0.45 ? 1 : 0);
  const mouth = individual(0.06);
  const voice = pitch * individual(0.08);
  const pulse = 17 + Math.random() * 5;
  let at = 0;
  for (let i = 0; i < grunts; i++) {
    const dur = ((0.34 - i * 0.04) / Math.sqrt(pitch)) * individual(0.1);
    voiceCall({
      at,
      dur,
      pitch: [[0, 62 * voice], [0.3, 54 * voice], [1, 48 * voice]],
      amp: [[0.04, 1], [0.65, 0.82]],
      vowel: [
        [0, [280 * mouth, 760 * mouth, 1500 * mouth]],
        [0.5, [380 * mouth, 900 * mouth, 1620 * mouth]],
        [1, [300 * mouth, 800 * mouth, 1500 * mouth]],
      ],
      q: [2.2, 2.8, 3.2],
      boost: [16, 12, 9],
      cutoff: 3800,
      air: 4,
      press: 0.45,
      jitter: 0.06,
      shimmer: 0.25,
      doubling: 0.62,
      chaos: [[0, 1, 0.3]],
      aspiration: 0.3,
      tremolo: [pulse, pulse - 4, 1],
      tremoloShape: 0.85,
      rasp: 0.5,
      gain: gain * 2.1 * (1 - i * 0.14),
      pan,
    });
    at += dur + 0.09 + Math.random() * 0.06;
  }
  noiseBurst(0.13, 0.1 * gain, 700, pan); // the snort that starts it
}

/**
 * Baaahhh. Lower and far wobblier than the goat's bleat: the vibrato starts
 * slow and accelerates, which is the whole character of a sheep, and two
 * formants park it on an open "aaa" instead of the goat's nasal honk.
 */
export function sfxSheepBaa(pitch = 1, gain = 1, pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) { if (!ctx) getCtx(); }
  if (settings.muted || !sfxBus || !ctx) return;
  const c = ctx;
  const t = c.currentTime;
  const dur = 0.92 / Math.sqrt(pitch);
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(240 * pitch, t);
  osc.frequency.linearRampToValueAtTime(214 * pitch, t + dur * 0.3);
  osc.frequency.linearRampToValueAtTime(186 * pitch, t + dur);

  // The wobble winds up: 7 Hz at the attack, 17 Hz by the tail.
  const wobble = c.createOscillator();
  wobble.type = 'sine';
  wobble.frequency.setValueAtTime(7, t);
  wobble.frequency.linearRampToValueAtTime(17, t + dur);
  const wobbleDepth = c.createGain();
  wobbleDepth.gain.setValueAtTime(4 * pitch, t);
  wobbleDepth.gain.linearRampToValueAtTime(26 * pitch, t + dur * 0.8);
  wobble.connect(wobbleDepth).connect(osc.frequency);

  // "b" closes the mouth at the attack, then it opens to a bleating "aaa".
  const f1 = c.createBiquadFilter();
  f1.type = 'bandpass';
  f1.Q.value = 3.2;
  f1.frequency.setValueAtTime(420 * pitch, t);
  f1.frequency.linearRampToValueAtTime(760 * pitch, t + dur * 0.22);
  f1.frequency.linearRampToValueAtTime(680 * pitch, t + dur);
  const f2 = c.createBiquadFilter();
  f2.type = 'bandpass';
  f2.Q.value = 4.5;
  f2.frequency.setValueAtTime(1250 * pitch, t);
  f2.frequency.linearRampToValueAtTime(1950 * pitch, t + dur * 0.26);
  f2.frequency.linearRampToValueAtTime(1700 * pitch, t + dur);

  const envelope = c.createGain();
  const peak = Math.max(0.02, 0.34 * gain);
  envelope.gain.setValueAtTime(0.0001, t);
  envelope.gain.exponentialRampToValueAtTime(peak, t + 0.06);
  envelope.gain.setValueAtTime(peak, t + dur * 0.62);
  envelope.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  const out = panInto(pan);
  osc.connect(f1).connect(envelope);
  osc.connect(f2).connect(envelope);
  envelope.connect(out);
  osc.start(t);
  wobble.start(t);
  osc.stop(t + dur + 0.08);
  wobble.stop(t + dur + 0.08);
  noiseBurst(0.05, 0.06 * gain, 1400, pan); // the breathy "b"
  osc.onended = () => {
    osc.disconnect(); wobble.disconnect(); wobbleDepth.disconnect();
    f1.disconnect(); f2.disconnect(); envelope.disconnect();
    if (out !== sfxBus) out.disconnect();
  };
}

/**
 * Oink.
 *
 * A pig grunt is measured at 60-250 ms with a fundamental down around 150-250
 * Hz, and the thing that defines it is how little of it is voice: the
 * harmonics-to-noise ratio of a pig grunt is famously low, so it is mostly
 * turbulence with a low pulse train buried in it. Snout resonances sit low and
 * close together. They come in bouts of two to five with uneven gaps, because
 * a metronome of grunts reads as a machine.
 *
 * The last grunt is the "oink" everyone actually pictures — longer, with the
 * pitch lifting before it falls away.
 */
export function sfxPigOink(pitch = 1, gain = 1, pan = 0): void {
  const grunts = 2 + (Math.random() < 0.55 ? 1 : 0);
  const mouth = individual(0.07);
  const voice = pitch * individual(0.07);
  let at = 0;
  for (let i = 0; i < grunts; i++) {
    const last = i === grunts - 1;
    const dur = ((last ? 0.23 : 0.12) / Math.sqrt(pitch)) * individual(0.12);
    const top = (232 - i * 16) * voice;
    voiceCall({
      at,
      dur,
      pitch: last
        ? [[0, top], [0.22, top * 1.24], [0.55, top * 0.92], [1, top * 0.52]]
        : [[0, top], [0.3, top * 0.86], [1, top * 0.58]],
      amp: last ? [[0.05, 0.86], [0.35, 1], [0.8, 0.5]] : [[0.04, 1], [0.55, 0.72]],
      vowel: [
        [0, [520 * mouth, 1180 * mouth, 2100 * mouth]],
        [1, [420 * mouth, 980 * mouth, 1950 * mouth]],
      ],
      q: [4, 4.5, 3.5],
      boost: [15, 12, 8],
      cutoff: 4400,
      air: 4,
      press: 0.65,
      jitter: 0.055,
      shimmer: 0.3,
      doubling: 0.45,
      chaos: [[0, 1, 0.45]],
      aspiration: 0.42,
      rasp: 0.5,
      gain: gain * 0.95 * (last ? 1 : 0.88 - i * 0.05),
      pan,
    });
    at += dur + 0.045 + Math.random() * 0.07;
  }
  noiseBurst(0.07, 0.12 * gain, 2600, pan); // the snort
}

/**
 * Hee-haw.
 *
 * The bray is two calls fighting: a strangled climbing inhale that is more air
 * than voice, then a wide-open falling honk that is all voice. Both halves sit
 * near the top of the scale for nonlinearity — donkey brays are a standard
 * textbook example of deterministic chaos in mammal vocalisation, and the
 * subharmonics under the honk are what stop it reading as a trumpet.
 *
 * Each cycle comes out shorter and wearier than the last, the way the real
 * thing runs out of animal rather than stopping.
 */
export function sfxDonkeyBray(pitch = 1, gain = 1, pan = 0): void {
  const cycles = 2 + (Math.random() < 0.55 ? 1 : 0);
  const voice = pitch * individual(0.06);
  const mouth = individual(0.05);
  let at = 0;
  for (let i = 0; i < cycles; i++) {
    const fade = 1 - i * 0.22;
    const hee = ((0.3 * (1 - i * 0.12)) / Math.sqrt(pitch)) * individual(0.08);
    const haw = ((0.42 * (1 - i * 0.1)) / Math.sqrt(pitch)) * individual(0.08);
    // The inhale: squeezed almost shut, and mostly wind through the gap.
    voiceCall({
      at,
      dur: hee,
      pitch: [[0, 250 * voice], [0.35, 470 * voice], [1, 580 * voice]],
      amp: [[0.15, 0.74], [0.75, 0.7]],
      vowel: [
        [0, [360 * mouth, 1950 * mouth, 2700 * mouth]],
        [1, [400 * mouth, 2200 * mouth, 2900 * mouth]],
      ],
      q: [5, 4.5, 4],
      boost: [9, 16, 12],
      cutoff: 6200,
      air: 4,
      press: 0.9,
      jitter: 0.06,
      shimmer: 0.25,
      doubling: 0.2,
      biphonation: 0.22,
      chaos: [[0.25, 1, 0.6]],
      aspiration: 0.85,
      tremolo: [30, 38, 0.35],
      rasp: 0.45,
      gain: gain * 0.6 * fade,
      pan,
    });
    // The honk: mouth wide, everything the lungs have, falling all the way
    // down to a creak that the chaos window turns into a proper rasp.
    voiceCall({
      at: at + hee + 0.012,
      dur: haw,
      pitch: [[0, 500 * voice], [0.18, 320 * voice], [0.6, 190 * voice], [1, 104 * voice]],
      amp: [[0.04, 1], [0.55, 0.92], [0.88, 0.46]],
      vowel: [
        [0, [700 * mouth, 1320 * mouth, 2400 * mouth]],
        [0.5, [620 * mouth, 1150 * mouth, 2300 * mouth]],
        [1, [430 * mouth, 950 * mouth, 2100 * mouth]],
      ],
      q: [3, 3.6, 4],
      boost: [16, 13, 10],
      cutoff: 5000,
      air: 3,
      press: 1,
      jitter: 0.05,
      shimmer: 0.2,
      doubling: 0.6,
      chaos: [[0.62, 1, 0.55]],
      jumps: [[0.82, 0.86]],
      aspiration: 0.2,
      tremolo: [24, 15, 0.3],
      rasp: 0.65,
      gain: gain * 0.6 * fade,
      pan,
    });
    at += hee + haw + 0.09;
  }
}

/**
 * The grey's call is the one thing on this list that is not an animal.
 *
 * Everything else here is built from a larynx because it came out of one; a
 * flying saucer has made the same sound since 1951 and that sound is a
 * theremin — heterodyning oscillators, no throat involved. So this is three
 * detuned sines swept together, beating against each other, with a formant
 * pair sliding over the top so it lands just short of saying a vowel. That
 * near-miss is what makes it uncanny rather than a menu bleep.
 */
export function sfxMartianWarble(pitch = 1, gain = 1, pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) { if (!ctx) getCtx(); }
  if (settings.muted || !sfxBus || !ctx) return;
  const c = ctx;
  const t = c.currentTime;
  const voice = pitch * individual(0.07);
  const dur = (0.88 / Math.sqrt(pitch)) * individual(0.1);
  const nodes: AudioNode[] = [];
  const out = panInto(pan);

  const env = c.createGain();
  // A too-quiet theremin under the off-world bed reads as no sound at all.
  const loudest = Math.max(0.03, 0.115 * gain);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(loudest, t + dur * 0.14);
  env.gain.exponentialRampToValueAtTime(loudest * 0.92, t + dur * 0.62);
  env.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  // Two formants over the sweep. They are what stop three sines sounding like
  // three sines: the ear hears a mouth shape moving and starts listening for
  // a word that never arrives.
  const f1 = c.createBiquadFilter();
  f1.type = 'peaking';
  f1.Q.value = 7;
  f1.gain.value = 12;
  f1.frequency.setValueAtTime(360, t);
  f1.frequency.linearRampToValueAtTime(700, t + dur * 0.45);
  f1.frequency.linearRampToValueAtTime(420, t + dur);
  const f2 = c.createBiquadFilter();
  f2.type = 'peaking';
  f2.Q.value = 6;
  f2.gain.value = 10;
  f2.frequency.setValueAtTime(1100, t);
  f2.frequency.linearRampToValueAtTime(2200, t + dur * 0.45);
  f2.frequency.linearRampToValueAtTime(1300, t + dur);
  f1.connect(f2).connect(env).connect(out);
  nodes.push(f1, f2, env);

  // The hand near the antenna: slow, wide, and speeding up as it withdraws.
  const vib = c.createOscillator();
  vib.type = 'sine';
  vib.frequency.setValueAtTime(5.2, t);
  vib.frequency.linearRampToValueAtTime(7.8, t + dur);
  vib.start(t);
  vib.stop(t + dur + 0.08);
  nodes.push(vib);

  const oscs: OscillatorNode[] = [];
  for (const [detune, level] of [[0, 1], [11, 0.6], [-17, 0.42]] as const) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.detune.value = detune;
    const base = 320 * voice;
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 1.85, t + dur * 0.42);
    osc.frequency.exponentialRampToValueAtTime(base * 1.5, t + dur * 0.72);
    osc.frequency.exponentialRampToValueAtTime(base * 1.14, t + dur);
    const depth = c.createGain();
    depth.gain.setValueAtTime(13 * voice, t);
    depth.gain.linearRampToValueAtTime(26 * voice, t + dur);
    vib.connect(depth).connect(osc.frequency);
    const mix = c.createGain();
    mix.gain.value = level;
    osc.connect(mix).connect(f1);
    osc.start(t);
    osc.stop(t + dur + 0.08);
    oscs.push(osc);
    nodes.push(osc, mix, depth);
  }
  oscs[0].onended = () => {
    for (const node of nodes) node.disconnect();
    if (out !== sfxBus) out.disconnect();
  };
}


/**
 * The moon cow is a cow in a fishbowl: the same recorded moo, pitched down and
 * slowed, then made to get out through a visor. The comb delay is the glass
 * four inches from its nose — without it, a slowed moo is just a slowed moo.
 */
function playHelmetMoo(rate: number, gain: number, pan: number): void {
  if (settings.muted) return;
  const buf = clipCache.get(VOICE.moo);
  if (!buf || buf === 'loading') {
    preloadVoiceClip(VOICE.moo);
    // No clip yet: the synthesised moo, pitched into the helmet's register.
    sfxCowMoo(rate * 0.9, gain, pan);
    return;
  }
  const c = getCtx();
  if (!sfxBus) return;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate;
  const hp = c.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 165;
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2050;
  const ring = c.createBiquadFilter();
  ring.type = 'peaking';
  ring.frequency.value = 620;
  ring.Q.value = 6;
  ring.gain.value = 5;
  const g = c.createGain();
  // The visor resonance and the comb both add level to an already-hot clip;
  // without this trim a moon cow arrives clipped where a cow does not.
  g.gain.value = gain * 0.55;
  const delay = c.createDelay(0.05);
  delay.delayTime.value = 0.011;
  const feedback = c.createGain();
  feedback.gain.value = 0.3;
  const out = panInto(pan);
  src.connect(hp).connect(lp).connect(ring).connect(g);
  g.connect(out);
  g.connect(delay);
  delay.connect(feedback).connect(delay);
  delay.connect(out);
  src.start();
  src.onended = () => {
    src.disconnect(); hp.disconnect(); lp.disconnect(); ring.disconnect();
    g.disconnect(); feedback.disconnect();
    // The helmet rings on after the moo stops; cutting it here would clip it.
    setTimeout(() => { delay.disconnect(); if (out !== sfxBus) out.disconnect(); }, 400);
  };
}

export function playAnimalCall(species: AnimalSpecies, rate = 1, gain = 1, pan = 0): void {
  if (species === 'goat') sfxGoatBleat(rate, gain, pan);
  else if (species === 'donkey') sfxDonkeyBray(rate, gain, pan);
  else if (species === 'reindeer') sfxReindeerGrunt(rate, gain, pan);
  else if (species === 'sheep') sfxSheepBaa(rate, gain, pan);
  else if (species === 'pig') sfxPigOink(rate, gain, pan);
  else if (species === 'martian') sfxMartianWarble(rate, gain, pan);
  else if (species === 'mooncow') playHelmetMoo(rate * 0.72, gain * 0.9, pan);
  else playMoo(rate, gain, pan);
}

export function sfxSplat(pan = 0): void {
  noiseBurst(0.22, 0.55, 650, pan);
  tone(180, 40, 0.3, 'sine', 0.4, 0, pan);
  tone(90, 30, 0.4, 'triangle', 0.35, 0.04, pan);
}

export function sfxDroneZap(pan = 0): void {
  tone(1400, 200, 0.16, 'square', 0.22, 0, pan);
  noiseBurst(0.1, 0.15, 3000, pan);
}

export function sfxHullHit(): void {
  noiseBurst(0.3, 0.5, 900);
  tone(120, 45, 0.4, 'triangle', 0.5);
}

/**
 * A clean electrical intake, deliberately distinct from the ordinary capture
 * pop. Two restrained rising stages read as core charge without turning every
 * animal pickup into a fanfare.
 */
export function sfxCoreRecharge(): void {
  tone(440, 760, 0.2, 'sine', 0.11, 0.06);
  tone(720, 1260, 0.28, 'triangle', 0.075, 0.14);
}

export function sfxWaveClear(flawless = false): void {
  tone(520, 780, 0.12, 'square', 0.2);
  tone(780, 1040, 0.14, 'square', 0.2, 0.12);
  tone(1040, 1560, 0.22, 'square', 0.22, 0.26);
  if (flawless) tone(1560, 2080, 0.3, 'triangle', 0.24, 0.46);
}

/** A short ascending ledger stamp when the live score overtakes its personal
 * best. Brighter than a combo tick, shorter than a wave-clear fanfare. */
export function sfxScoreRecord(): void {
  tone(660, 990, 0.11, 'triangle', 0.18);
  tone(990, 1485, 0.14, 'triangle', 0.17, 0.1);
  tone(1320, 1980, 0.2, 'sine', 0.14, 0.22);
}

export function sfxComboUp(level: number): void {
  const base = 600 + level * 140;
  tone(base, base * 1.5, 0.14, 'triangle', 0.3);
}

let lastRustle = 0;
export function sfxRustle(): void {
  const now = performance.now();
  if (now - lastRustle < 180) return;
  lastRustle = now;
  noiseBurst(0.14, 0.08, 1800);
}

let lastRumble = 0;
export function sfxRumble(): void {
  const now = performance.now();
  if (now - lastRumble < 220) return;
  lastRumble = now;
  noiseBurst(0.22, 0.14, 260);
  tone(70, 50, 0.2, 'triangle', 0.12);
}

let lastBeamHit = 0;
/** Bright confirmation tick when the tractor beam locks onto a hostile. */
export function sfxBeamHit(pan = 0): void {
  const now = performance.now();
  if (now - lastBeamHit < 90) return;
  lastBeamHit = now;
  tone(1100, 520, 0.1, 'square', 0.16, 0, pan);
  noiseBurst(0.08, 0.1, 4200, pan);
}

/** Three restrained rising notes mark the gates before the capture pop. */
export function sfxBeamGate(stage: number): void {
  const base = 430 + stage * 135;
  tone(base, base * 1.16, 0.075, 'sine', 0.09);
}

export function sfxExplode(pan = 0): void {
  noiseBurst(0.55, 0.75, 420, pan);
  noiseBurst(0.12, 0.4, 3600, pan);
  tone(110, 28, 0.6, 'triangle', 0.6, 0, pan);
  tone(60, 24, 0.8, 'sine', 0.5, 0.05, pan);
}

/** The drive winding down during the final dive. This deliberately starts
 * after the ordinary flight beds are silenced: death should change the sound,
 * not leave a conspicuous second and a half of dead air before impact. */
export function sfxCrashFall(pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) return;
  const c = ctx;
  const t = c.currentTime;
  const out = panInto(pan);
  const drive = c.createOscillator();
  const wobble = c.createOscillator();
  const wobbleGain = c.createGain();
  const filter = c.createBiquadFilter();
  const gain = c.createGain();
  drive.type = 'sawtooth';
  drive.frequency.setValueAtTime(390, t);
  drive.frequency.exponentialRampToValueAtTime(52, t + 1.58);
  wobble.type = 'sine';
  wobble.frequency.setValueAtTime(8.5, t);
  wobble.frequency.linearRampToValueAtTime(3.2, t + 1.58);
  wobbleGain.gain.setValueAtTime(42, t);
  wobbleGain.gain.linearRampToValueAtTime(11, t + 1.58);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1900, t);
  filter.frequency.exponentialRampToValueAtTime(230, t + 1.58);
  filter.Q.value = 2.4;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.2, t + 0.035);
  gain.gain.setValueAtTime(0.16, t + 1.28);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.62);
  wobble.connect(wobbleGain).connect(drive.frequency);
  drive.connect(filter).connect(gain).connect(out);
  drive.start(t);
  wobble.start(t);
  drive.stop(t + 1.67);
  wobble.stop(t + 1.67);
  noiseBurst(1.45, 0.065, 2600, pan);
  drive.onended = () => {
    drive.disconnect();
    wobble.disconnect();
    wobbleGain.disconnect();
    filter.disconnect();
    gain.disconnect();
    if (out !== sfxBus) out.disconnect();
  };
}

/** Dirt, hull shear and a sub-bass body hit for the hillside strike. Ordinary
 * explosions stay punchy and short; the fatal impact needs weight and earth. */
export function sfxCrashImpact(pan = 0): void {
  noiseBurst(0.9, 0.9, 280, pan);
  noiseBurst(0.16, 0.48, 4800, pan);
  tone(680, 70, 0.36, 'sawtooth', 0.18, 0, pan);
  tone(96, 18, 1.25, 'triangle', 0.76, 0, pan);
  tone(45, 12, 1.55, 'sine', 0.48, 0.04, pan);
}

export function sfxDrop(pan = 0): void {
  tone(700, 180, 0.25, 'sine', 0.2, 0, pan);
}

export function sfxFeverStart(): void {
  tone(220, 880, 0.45, 'sawtooth', 0.28);
  tone(330, 1320, 0.5, 'triangle', 0.2, 0.07);
  tone(440, 1760, 0.6, 'square', 0.14, 0.15);
  noiseBurst(0.4, 0.12, 5200);
}

export function sfxBossDown(): void {
  sfxExplode();
  tone(880, 55, 1.1, 'sawtooth', 0.4, 0.05);
  noiseBurst(0.8, 0.5, 300);
}

export function sfxSplash(pan = 0): void {
  noiseBurst(0.3, 0.4, 1400, pan);
  tone(500, 90, 0.35, 'sine', 0.3, 0, pan);
  noiseBurst(0.5, 0.14, 700, pan);
}

/** Crack + rolling rumble; `near` strikes hit harder. */
export function sfxThunder(near: boolean, pan = 0): void {
  noiseBurst(0.09, near ? 0.85 : 0.4, 6800, pan);
  noiseBurst(1.4, near ? 0.6 : 0.3, 240, pan);
  tone(70, 26, 1.6, 'triangle', near ? 0.5 : 0.25, 0.05, pan);
  tone(52, 22, 2.2, 'sine', near ? 0.4 : 0.2, 0.3, pan);
}

/* ---------------- v0.8 event & power-up SFX ---------------- */

/** Incoming meteor: a falling whistle over crackling air. */
export function sfxMeteorFall(pan = 0): void {
  tone(2400, 260, 1.05, 'sawtooth', 0.09, 0, pan);
  tone(1800, 180, 1.1, 'sine', 0.12, 0.05, pan);
  noiseBurst(0.9, 0.06, 5200, pan);
}

/** Meteor impact: deeper and dirtier than a building pop. */
export function sfxMeteorImpact(pan = 0): void {
  noiseBurst(0.7, 0.85, 300, pan);
  noiseBurst(0.14, 0.5, 4200, pan);
  tone(90, 22, 0.9, 'triangle', 0.7, 0, pan);
  tone(48, 18, 1.2, 'sine', 0.55, 0.06, pan);
}

/** The singularity opens: a long swallowed womp. */
export function sfxBlackHoleOpen(): void {
  tone(160, 24, 1.4, 'sine', 0.5);
  tone(320, 30, 1.2, 'triangle', 0.3, 0.05);
  noiseBurst(0.8, 0.2, 240);
}

/** One soul crossing the event horizon. */
export function sfxGulp(pitch = 1): void {
  tone(420 * pitch, 70 * pitch, 0.22, 'sine', 0.32);
  tone(640 * pitch, 90 * pitch, 0.18, 'triangle', 0.18, 0.03);
}

/** EMP: bright crack, then everything electronic sighs. */
export function sfxEmpBlast(): void {
  noiseBurst(0.12, 0.5, 8000);
  tone(3200, 120, 0.5, 'square', 0.26, 0);
  tone(1600, 60, 0.7, 'sawtooth', 0.2, 0.05);
  tone(62, 30, 0.9, 'sine', 0.5, 0.02);
}

/** Convoy air horn: two-tone lorry blast. */
export function sfxTruckHorn(pan = 0): void {
  tone(340, 336, 0.42, 'sawtooth', 0.16, 0, pan);
  tone(425, 420, 0.42, 'sawtooth', 0.14, 0, pan);
  tone(340, 336, 0.55, 'sawtooth', 0.16, 0.55, pan);
  tone(425, 420, 0.55, 'sawtooth', 0.14, 0.55, pan);
}

/** Parachute pop when a rained cow touches down. */
export function sfxChutePop(pan = 0): void {
  noiseBurst(0.08, 0.2, 2600, pan);
  tone(500, 260, 0.14, 'sine', 0.16, 0, pan);
}

/** Shield ignition: rising charge, crystalline lock-in shimmer. */
export function sfxShieldUp(): void {
  tone(180, 720, 0.35, 'sawtooth', 0.16);
  tone(360, 1440, 0.4, 'triangle', 0.14, 0.05);
  tone(1760, 1720, 0.5, 'sine', 0.1, 0.28);
  tone(2200, 2160, 0.45, 'sine', 0.07, 0.32);
  noiseBurst(0.25, 0.06, 6400, 0);
}

/** The tour moves on: ascending departure sweep. */
export function sfxDepart(): void {
  tone(220, 880, 0.7, 'triangle', 0.22);
  tone(330, 1320, 0.8, 'sine', 0.16, 0.1);
  tone(110, 440, 0.9, 'sawtooth', 0.1, 0.05);
  noiseBurst(0.7, 0.08, 4200);
}

/** Seven lands banked: a long, unmistakable ascent instead of another
 * departure sting. Procedural so the ending cannot stall on an asset fetch. */
export function sfxTourComplete(): void {
  const notes = [220, 277.18, 329.63, 440, 554.37, 659.25];
  for (let i = 0; i < notes.length; i++) {
    tone(notes[i], notes[i] * 1.015, 1.35, i % 2 ? 'sine' : 'triangle', 0.11, i * 0.38);
  }
  tone(110, 880, 3.7, 'sawtooth', 0.07);
  tone(880, 1320, 1.2, 'sine', 0.14, 2.2);
  noiseBurst(2.8, 0.045, 6200);
}

/** Holy synth choir: a slow major swell for rituals and full congregations. */
export function sfxChoir(): void {
  if (settings.muted || !sfxBus || !ctx) return;
  const t = ctx.currentTime;
  const freqs = [220, 277.18, 329.63, 440, 554.37];
  for (let i = 0; i < freqs.length; i++) {
    for (const detune of [-4, 4]) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freqs[i];
      osc.detune.value = detune;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.05, t + 0.55 + i * 0.06);
      g.gain.setValueAtTime(0.05, t + 1.6);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 3);
      osc.connect(g).connect(sfxBus);
      osc.start(t);
      osc.stop(t + 3.1);
      osc.onended = () => { osc.disconnect(); g.disconnect(); };
    }
  }
}

/** A far-off wolf greets the blood moon. */
export function sfxHowl(pan = 0): void {
  if (settings.muted || !sfxBus || !ctx) return;
  const c = ctx;
  const t = c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(290, t);
  osc.frequency.linearRampToValueAtTime(540, t + 0.5);
  osc.frequency.setValueAtTime(540, t + 1.0);
  osc.frequency.linearRampToValueAtTime(380, t + 1.9);
  const vib = c.createOscillator();
  vib.frequency.value = 5.5;
  const vibG = c.createGain();
  vibG.gain.value = 9;
  vib.connect(vibG).connect(osc.frequency);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.11, t + 0.35);
  g.gain.setValueAtTime(0.11, t + 1.2);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 2);
  const out = panInto(pan);
  osc.connect(g).connect(out);
  osc.start(t);
  vib.start(t);
  osc.stop(t + 2.1);
  vib.stop(t + 2.1);
  osc.onended = () => { osc.disconnect(); vib.disconnect(); g.disconnect(); if (out !== sfxBus) out.disconnect(); };
}

let rainNodes: { src: AudioBufferSourceNode; gain: GainNode } | null = null;

export function startRainLoop(): void {
  if (rainNodes) return;
  const c = getCtx();
  if (!sfxBus) return;
  const len = c.sampleRate * 2;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2400;
  filter.Q.value = 0.5;
  const gain = c.createGain();
  gain.gain.value = 0.0001;
  gain.gain.setTargetAtTime(0.05, c.currentTime, 1.2);
  src.connect(filter).connect(gain).connect(sfxBus);
  src.start();
  rainNodes = { src, gain };
}

export function stopRainLoop(): void {
  if (!rainNodes || !ctx) return;
  const { src, gain } = rainNodes;
  rainNodes = null;
  gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.8);
  setTimeout(() => {
    try { src.stop(); } catch { /* already stopped */ }
    src.disconnect();
    gain.disconnect();
  }, 3000);
}

let holeOsc: OscillatorNode | null = null;
let holeGain: GainNode | null = null;
let holeLfo: OscillatorNode | null = null;

/** Low churning drone while a black hole is open. */
export function setBlackHoleHum(active: boolean): void {
  if (!ctx || !sfxBus) { if (active) getCtx(); }
  if (!ctx || !sfxBus) return;
  if (active && !holeOsc) {
    holeOsc = ctx.createOscillator();
    holeGain = ctx.createGain();
    holeLfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    holeOsc.type = 'triangle';
    holeOsc.frequency.value = 38;
    holeLfo.type = 'sine';
    holeLfo.frequency.value = 1.7;
    lfoGain.gain.value = 7;
    holeLfo.connect(lfoGain).connect(holeOsc.frequency);
    holeGain.gain.value = 0.0001;
    holeGain.gain.setTargetAtTime(settings.muted ? 0 : 0.2, ctx.currentTime, 0.2);
    holeOsc.connect(holeGain).connect(sfxBus);
    holeOsc.start();
    holeLfo.start();
  } else if (!active && holeOsc) {
    const osc = holeOsc;
    const lfo = holeLfo;
    const g = holeGain;
    holeOsc = null;
    holeLfo = null;
    holeGain = null;
    if (g && ctx) g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.15);
    setTimeout(() => {
      try { osc.stop(); lfo?.stop(); } catch { /* already stopped */ }
      osc.disconnect();
      lfo?.disconnect();
      g?.disconnect();
    }, 800);
  }
}

type BeamHumRuntime = (ctx: AudioContext, bus: GainNode, active: boolean, progress: number) => void;
let beamHumRuntime: BeamHumRuntime | null = null;
let beamHumLoad: Promise<void> | null = null;
let beamHumActive = false;
let beamHumProgress = 0;

/** One saucer oscillator does both jobs. In flight it is a restrained triangular
 *  hover/engine tone driven by real speed; opening the cone turns the same voice
 *  into the familiar sawtooth beam thrum. Actual cargo then raises its pitch and
 *  level against honest lift progress. */
export function setBeamHum(active: boolean, progress = 0): void {
  beamHumActive = active;
  beamHumProgress = progress;
  if (active && settings.muted) return;
  if (beamHumRuntime) {
    if (ctx && sfxBus) beamHumRuntime(ctx, sfxBus, active, progress);
    return;
  }
  if (!active) return;
  const c = getCtx();
  const bus = sfxBus!;
  beamHumLoad ??= import('./flight-audio').then(({ default: update }) => {
    beamHumRuntime = update;
    update(c, bus, beamHumActive, beamHumProgress);
  }).catch(() => {
    // A transient chunk failure should silence this frame, not poison retries.
    beamHumLoad = null;
  });
}

/* ---------------- rotor beds: you hear them coming ---------------- */
// A patrol used to announce itself only by the zap of a shot already fired.
// Each drone now carries a rotor bed whose blade-pass rate, brightness and
// level all track its distance, panned by world x — so the sound of one
// closing in is a warning you can act on. Deliberately quiet: the ceiling is
// one twentieth of a moo, and a crowd of drones is divided down, not summed
// into a wall of buzz.

const ROTOR_AUDIBLE_R = 46;
const ROTOR_PEAK = 0.05;
const ROTOR_SMOOTH = 0.12;
const ROTOR_TICK_S = 0.05; // 20 Hz param writes; the sim runs at 60

interface RotorVoice {
  osc: OscillatorNode;
  blade: OscillatorNode;
  bladeDepth: GainNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  panner: StereoPannerNode | null;
}

export interface RotorSource {
  /** Distance from the listener (the saucer), in world units. */
  dist: number;
  /** -1..1 stereo position. */
  pan: number;
  stunned?: boolean;
}

const rotorVoices: RotorVoice[] = [];
let rotorTick = -1;

function makeRotorVoice(): RotorVoice | null {
  const c = getCtx();
  if (!sfxBus) return null;
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.value = 44;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 300;
  filter.Q.value = 0.8;
  const gain = c.createGain();
  gain.gain.value = 0.0001;
  const blade = c.createOscillator();
  blade.type = 'sine';
  blade.frequency.value = 14;
  const bladeDepth = c.createGain();
  bladeDepth.gain.value = 0;
  blade.connect(bladeDepth).connect(gain.gain);
  const panner = typeof c.createStereoPanner === 'function' ? c.createStereoPanner() : null;
  osc.connect(filter).connect(gain);
  if (panner) gain.connect(panner).connect(sfxBus);
  else gain.connect(sfxBus);
  osc.start();
  blade.start();
  return { osc, blade, bladeDepth, filter, gain, panner };
}

function freeRotorVoice(voice: RotorVoice): void {
  if (ctx) voice.gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.05);
  setTimeout(() => {
    try { voice.osc.stop(); voice.blade.stop(); } catch { /* already stopped */ }
    voice.osc.disconnect();
    voice.blade.disconnect();
    voice.bladeDepth.disconnect();
    voice.filter.disconnect();
    voice.gain.disconnect();
    voice.panner?.disconnect();
  }, 260);
}

/** Called every frame with the live patrol; allocates and frees voices to match. */
export function updateRotorField(sources: RotorSource[]): void {
  if (settings.muted || !unlocked) { if (rotorVoices.length) stopRotorField(); return; }
  if (sources.length === 0) { if (rotorVoices.length) stopRotorField(); return; }
  const c = getCtx();
  if (!sfxBus) return;
  if (rotorTick > 0 && c.currentTime - rotorTick < ROTOR_TICK_S) return;
  rotorTick = c.currentTime;
  while (rotorVoices.length > sources.length) freeRotorVoice(rotorVoices.pop()!);
  while (rotorVoices.length < sources.length) {
    const voice = makeRotorVoice();
    if (!voice) break;
    rotorVoices.push(voice);
  }
  // Many rotors share one budget: a swarm gets busier, not louder.
  const crowd = 1 / Math.sqrt(Math.max(1, rotorVoices.length));
  for (let i = 0; i < rotorVoices.length; i++) {
    const voice = rotorVoices[i];
    const src = sources[i];
    const near = Math.max(0, 1 - src.dist / ROTOR_AUDIBLE_R);
    const level = near * near * (src.stunned ? 0.45 : 1);
    const body = ROTOR_PEAK * level * crowd;
    const t = c.currentTime;
    voice.gain.gain.setTargetAtTime(Math.max(0.00005, body), t, ROTOR_SMOOTH);
    voice.bladeDepth.gain.setTargetAtTime(body * 0.7, t, ROTOR_SMOOTH);
    voice.blade.frequency.setTargetAtTime((13 + level * 9) * (src.stunned ? 0.35 : 1), t, ROTOR_SMOOTH);
    voice.osc.frequency.setTargetAtTime(42 + level * 16, t, ROTOR_SMOOTH);
    voice.filter.frequency.setTargetAtTime(260 + level * 2400, t, ROTOR_SMOOTH);
    voice.panner?.pan.setTargetAtTime(Math.max(-1, Math.min(1, src.pan)), t, ROTOR_SMOOTH);
  }
}

export function stopRotorField(): void {
  while (rotorVoices.length) freeRotorVoice(rotorVoices.pop()!);
  rotorTick = -1;
}

let heli: { src: AudioBufferSourceNode; thrum: OscillatorNode; slap: OscillatorNode; gain: GainNode } | null = null;

/** THE AGENCY's blade slap: filtered noise chopped by a deep 5 Hz tremolo. */
export function setHeliRotor(want: boolean, dist = 0): void {
  const active = want && !settings.muted && unlocked;
  if (!active && !heli) return;
  if (active && !heli) {
    const c = getCtx();
    if (!sfxBus) return;
    const len = c.sampleRate * 2;
    const buf = c.createBuffer(1, len, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const chop = c.createBiquadFilter();
    chop.type = 'bandpass';
    chop.frequency.value = 520;
    chop.Q.value = 1.1;
    const gain = c.createGain();
    gain.gain.value = 0.0001;
    const slap = c.createOscillator();
    slap.type = 'sine';
    slap.frequency.value = 5.2;
    const slapDepth = c.createGain();
    slapDepth.gain.value = 0.05;
    slap.connect(slapDepth).connect(gain.gain);
    const thrum = c.createOscillator();
    thrum.type = 'triangle';
    thrum.frequency.value = 26;
    const thrumGain = c.createGain();
    thrumGain.gain.value = 0.35;
    src.connect(chop).connect(gain);
    thrum.connect(thrumGain).connect(gain);
    gain.connect(sfxBus);
    src.start();
    slap.start();
    thrum.start();
    heli = { src, thrum, slap, gain };
  }
  if (active && heli && ctx) {
    const near = Math.max(0, 1 - dist / 70);
    heli.gain.gain.setTargetAtTime(Math.max(0.00005, 0.075 * near * near), ctx.currentTime, 0.2);
  } else if (!active && heli) {
    const { src, thrum, slap, gain } = heli;
    heli = null;
    if (ctx) gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.2);
    setTimeout(() => {
      try { src.stop(); thrum.stop(); slap.stop(); } catch { /* already stopped */ }
      src.disconnect();
      thrum.disconnect();
      slap.disconnect();
      gain.disconnect();
    }, 900);
  }
}

/** A drone has picked its shot: two rising blips, quiet enough to live under
 *  the music but sharp enough to make you move. */
let lastLock = 0;
export function sfxDroneLock(pan = 0): void {
  const now = performance.now();
  if (now - lastLock < 260) return;
  lastLock = now;
  tone(760, 1180, 0.07, 'square', 0.055, 0, pan);
  tone(1180, 1560, 0.06, 'square', 0.045, 0.075, pan);
}

/* ---------------- the grid: mains hum and arcing wires ---------------- */

let gridOsc: OscillatorNode | null = null;
let gridHarm: OscillatorNode | null = null;
let gridGain: GainNode | null = null;

/** 0..1 proximity to a live span. Fifty-hertz hum, doubled at the harmonic. */
export function setGridHum(want: number): void {
  const level = settings.muted || !unlocked ? 0 : Math.max(0, Math.min(1, want));
  if (level <= 0.001 && !gridOsc) return;
  if (level > 0.001 && !gridOsc) {
    const c = getCtx();
    if (!sfxBus) return;
    gridGain = c.createGain();
    gridGain.gain.value = 0.0001;
    gridOsc = c.createOscillator();
    gridOsc.type = 'sawtooth';
    gridOsc.frequency.value = 50;
    gridHarm = c.createOscillator();
    gridHarm.type = 'sine';
    gridHarm.frequency.value = 150;
    const harmGain = c.createGain();
    harmGain.gain.value = 0.4;
    const shape = c.createBiquadFilter();
    shape.type = 'lowpass';
    shape.frequency.value = 900;
    gridOsc.connect(shape).connect(gridGain);
    gridHarm.connect(harmGain).connect(gridGain);
    gridGain.connect(sfxBus);
    gridOsc.start();
    gridHarm.start();
  }
  if (gridGain && ctx) {
    gridGain.gain.setTargetAtTime(Math.max(0.00005, 0.05 * level), ctx.currentTime, 0.25);
  }
  if (level <= 0.001 && gridOsc) {
    const osc = gridOsc;
    const harm = gridHarm;
    const gain = gridGain;
    gridOsc = null;
    gridHarm = null;
    gridGain = null;
    setTimeout(() => {
      try { osc.stop(); harm?.stop(); } catch { /* already stopped */ }
      osc.disconnect();
      harm?.disconnect();
      gain?.disconnect();
    }, 700);
  }
}

/** Hull against a live conductor: a fat mains-frequency arc and a bright crack. */
export function sfxArc(pan = 0): void {
  noiseBurst(0.18, 0.45, 7200, pan);
  tone(100, 96, 0.3, 'sawtooth', 0.3, 0, pan);
  tone(200, 190, 0.26, 'square', 0.2, 0.02, pan);
  tone(2600, 380, 0.2, 'sawtooth', 0.16, 0, pan);
  noiseBurst(0.4, 0.16, 1100, pan);
}

/* ---------------- voice clips ---------------- */

const clipCache = new Map<string, AudioBuffer | 'loading'>();
const clipPromises = new Map<string, Promise<void>>();

let currentVoiceWarmScope: string | null = null;

export function preloadVoiceClip(url: string, priority = false, scope?: string): void {
  if (clipCache.has(url)) return;
  if (scope) currentVoiceWarmScope = scope;
  void import('./flight-audio').then(
    (runtime) => {
      if (scope && scope !== currentVoiceWarmScope) return;
      runtime.queueVoiceWarm(url, preloadVoiceClipAsync, priority, scope);
    },
    () => undefined,
  );
}

/** Awaitable preload — the boot loader tracks these for its progress bar. */
export function preloadVoiceClipAsync(url: string): Promise<void> {
  const existing = clipPromises.get(url);
  if (existing) return existing;
  if (clipCache.get(url) instanceof AudioBuffer) return Promise.resolve();
  const c = getCtx();
  clipCache.set(url, 'loading');
  const p = fetch(url)
    .then((r) => r.arrayBuffer())
    .then((ab) => c.decodeAudioData(ab))
    .then((buf) => { clipCache.set(url, buf); })
    .catch(() => { clipCache.delete(url); clipPromises.delete(url); });
  clipPromises.set(url, p);
  return p;
}

// Spoken lines are serialised: one voice at a time. A line that arrives while
// someone is talking waits its turn briefly, then is dropped — a delayed
// announcement whose moment has passed reads worse than silence. Short
// reaction stings (moo, wuh, whoo) stay free-firing; they aren't speech.
const SPEECH_MAX_WAIT_S = 4;
const SPEECH_QUEUE_MAX = 2;
let speechCurrent: string | null = null;
const speechQueue: Array<{
  url: string; gain: number; rate: number; pan: number; queuedAt: number;
  finished?: () => void; keepQueued: boolean; stillValid?: () => boolean;
}> = [];

export function playVoiceClip(
  url: string, gain = 1, rate = 1, pan = 0, finished?: () => void, keepQueued = false,
  stillValid?: () => boolean,
): void {
  if (stillValid && !stillValid()) { finished?.(); return; }
  if (settings.muted) { finished?.(); return; }
  if (isSting(url)) { startVoiceClip(url, gain, rate, pan, finished, false, false, stillValid); return; }
  if (speechCurrent) {
    // Someone is already talking. Drop echoes of the same line, queue the rest.
    if (speechCurrent === url || speechQueue.some((q) => q.url === url)) { finished?.(); return; }
    if (speechQueue.length >= SPEECH_QUEUE_MAX) {
      const expendable = speechQueue.findIndex((queued) => !queued.keepQueued);
      const [dropped] = speechQueue.splice(expendable >= 0 ? expendable : 0, 1);
      dropped?.finished?.();
    }
    speechQueue.push({ url, gain, rate, pan, queuedAt: getCtx().currentTime, finished, keepQueued, stillValid });
    return;
  }
  startVoiceClip(url, gain, rate, pan, finished, keepQueued, false, stillValid);
}

/** The patron thank-you is a presentation beat, not an in-world pickup. Give
 * it the roomy mothership echo the confirmation screen originally promised
 * without making every groupie voice sound distant. */
export function playEchoedVoiceClip(url: string, gain = 1, rate = 1, pan = 0): void {
  if (settings.muted) return;
  startVoiceClip(url, gain, rate, pan, undefined, false, true);
}

/** Resolves at actual source end, including any wait behind the current announcer line. */
export function playVoiceClipTracked(url: string, gain = 1, rate = 1, pan = 0): Promise<void> {
  return new Promise((resolve) => playVoiceClip(url, gain, rate, pan, resolve, true));
}

function startVoiceClip(
  url: string, gain: number, rate: number, pan: number, finished?: () => void, keepQueued = false,
  echoed = false, stillValid?: () => boolean,
): void {
  if (stillValid && !stillValid()) { finished?.(); return; }
  const buf = clipCache.get(url);
  if (!buf || buf === 'loading') {
    // Asset warm-up is an optimisation, not a correctness boundary. A voice
    // requested before its decode completes used to vanish permanently.
    const requestedAt = getCtx().currentTime;
    void preloadVoiceClipAsync(url).then(() => {
      if (!ctx || ctx.currentTime - requestedAt > SPEECH_MAX_WAIT_S || (stillValid && !stillValid())) {
        finished?.();
        return;
      }
      const loaded = clipCache.get(url);
      if (loaded && loaded !== 'loading') {
        if (echoed) playEchoedVoiceClip(url, gain, rate, pan);
        else playVoiceClip(url, gain, rate, pan, finished, keepQueued, stillValid);
      }
      else finished?.();
    });
    return;
  }
  const c = getCtx();
  if (!sfxBus) { finished?.(); return; }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate;
  const g = c.createGain();
  g.gain.value = gain;
  const out = panInto(pan);
  src.connect(g).connect(out);
  let echoDelay: DelayNode | null = null;
  let echoFilter: BiquadFilterNode | null = null;
  let echoWet: GainNode | null = null;
  let echoFeedback: GainNode | null = null;
  if (echoed) {
    echoDelay = c.createDelay(1);
    echoDelay.delayTime.value = 0.24;
    echoFilter = c.createBiquadFilter();
    echoFilter.type = 'lowpass';
    echoFilter.frequency.value = 3100;
    echoWet = c.createGain();
    echoWet.gain.value = 0.42;
    echoFeedback = c.createGain();
    echoFeedback.gain.value = 0.28;
    g.connect(echoDelay);
    echoDelay.connect(echoFilter).connect(echoWet).connect(out);
    echoFilter.connect(echoFeedback).connect(echoDelay);
  }
  // Duck the music under speech.
  if (musicBus) {
    musicBus.gain.setTargetAtTime(0.28, c.currentTime, 0.05);
    musicBus.gain.setTargetAtTime(1, c.currentTime + Math.min(buf.duration, 3), 0.4);
  }
  const speech = !isSting(url);
  if (speech) speechCurrent = url;
  src.start();
  src.onended = () => {
    src.disconnect();
    const releaseNodes = () => {
      g.disconnect();
      echoDelay?.disconnect();
      echoFilter?.disconnect();
      echoWet?.disconnect();
      echoFeedback?.disconnect();
      if (out !== sfxBus) out.disconnect();
    };
    if (echoed) setTimeout(releaseNodes, 1500);
    else releaseNodes();
    if (speech && speechCurrent === url) {
      speechCurrent = null;
      drainSpeechQueue();
    }
    finished?.();
  };
}

function drainSpeechQueue(): void {
  if (settings.muted) {
    for (const queued of speechQueue.splice(0)) queued.finished?.();
    return;
  }
  const now = getCtx().currentTime;
  while (speechQueue.length > 0) {
    const next = speechQueue.shift()!;
    if ((next.stillValid && !next.stillValid()) || (!next.keepQueued && now - next.queuedAt > SPEECH_MAX_WAIT_S)) {
      next.finished?.(); // the moment has passed
      continue;
    }
    startVoiceClip(next.url, next.gain, next.rate, next.pan, next.finished, next.keepQueued, false, next.stillValid);
    return;
  }
}

function isSting(url: string): boolean {
  // Short reactions are immediate; announcer/member and longer GPT lines queue.
  return !url.includes('/tts/') && !url.includes('/gpt-');
}

export const THANKS_VOICES = ['/sfx/thanks-fren.m4a', '/sfx/thanks-legend.m4a'] as const;

export const VOICE = {
  moo: '/sfx/moo.m4a',
  notACult: '/sfx/not-a-cult.m4a',
  fiatNam: '/sfx/gpt-600b-fiat-nam.m4a',
  allTimeHigh: '/sfx/gpt-600b-all-time-high.m4a',
  meme: '/sfx/gpt-600b-meme.m4a',
  timeLock: '/sfx/gpt-600b-time-lock.m4a',
  fourTwenty: '/sfx/four-twenty.m4a',
  wuh: '/sfx/sting-wuh.m4a',
  whoo: '/sfx/sting-whoo.m4a',
  // Mothership announcer (OpenAI TTS, generated at build time).
  ttsWelcome: '/sfx/tts/welcome.m4a',
  ttsStone: '/sfx/tts/stone.m4a',
  ttsHoldBeam: '/sfx/tts/hold-beam.m4a',
  ttsStorm: '/sfx/tts/storm.m4a',
  ttsTaxman: '/sfx/tts/taxman.m4a',
  ttsBadge: '/sfx/tts/badge.m4a',
  ttsGameover: '/sfx/tts/gameover.m4a',
  ttsBuildings: '/sfx/tts/buildings.m4a',
  // v0.9 event lines — generated by tools/build-tts.mjs.
  ttsConvoy: '/sfx/tts/convoy.m4a',
  ttsBloodmoon: '/sfx/tts/bloodmoon.m4a',
  ttsMeteor: '/sfx/tts/meteor.m4a',
  ttsBiomeValley: '/sfx/tts/biome-valley.m4a',
  ttsBiomeMesa: '/sfx/tts/biome-mesa.m4a',
  ttsBiomeSnow: '/sfx/tts/biome-snow.m4a',
  // v0.14 lands.
  ttsBiomeMoor: '/sfx/tts/biome-moor.m4a',
  ttsBiomeTown: '/sfx/tts/biome-town.m4a',
  ttsPylons: '/sfx/tts/pylons.m4a',
  // v0.10 tour + patron lines.
  ttsDepart: '/sfx/tts/depart.m4a',
  ttsBlessed: '/sfx/tts/blessed.m4a',
  ttsBiomeMars: '/sfx/tts/biome-mars.m4a',
  ttsBiomeMoon: '/sfx/tts/biome-moon.m4a',
  ttsLowG: '/sfx/tts/lowg.m4a',
} as const;

/** The announcer's "tonight's harvest" line per land. One table, so the run
 *  start, the departure and the loader cannot drift from each other. */
export const BIOME_VOICE: Record<BiomeId, string> = {
  valley: VOICE.ttsBiomeValley,
  mesa: VOICE.ttsBiomeMesa,
  snow: VOICE.ttsBiomeSnow,
  moor: VOICE.ttsBiomeMoor,
  town: VOICE.ttsBiomeTown,
  mars: VOICE.ttsBiomeMars,
  moon: VOICE.ttsBiomeMoon,
};

/** The announcer's per-member reveal line ("dee en eye. Found.").
 *  Generated by tools/build-tts.mjs from the roster; preload at spawn time. */
export function memberVoice(id: string): string {
  return `/sfx/tts/member-${id}.m4a`;
}

/* ---------------- biome ambience ---------------- */

export interface AmbientProfile {
  wind: number;
  owl: boolean;
  insects: boolean;
  frogs: boolean;
}

const AMBIENT_PROFILES: Record<BiomeId, AmbientProfile> = {
  valley: { wind: 0.05, owl: true, insects: true, frogs: true },
  mesa: { wind: 0.035, owl: true, insects: true, frogs: true },
  snow: { wind: 0.065, owl: true, insects: false, frogs: false },
  moor: { wind: 0.075, owl: true, insects: true, frogs: true },
  town: { wind: 0.025, owl: false, insects: true, frogs: true },
  // Mars has enough atmosphere for a faint filtered wind, but no Earth night
  // chorus. The Moon is airless: its music, machinery and herd calls sit over
  // actual silence instead of inexplicable owls and crickets.
  mars: { wind: 0.015, owl: false, insects: false, frogs: false },
  moon: { wind: 0, owl: false, insects: false, frogs: false },
};

export function ambientProfileForBiome(biome: BiomeId): Readonly<AmbientProfile> {
  return AMBIENT_PROFILES[biome];
}

let ambientBiome: BiomeId = 'valley';
let windGain: GainNode | null = null;

export function setAmbientBiome(biome: BiomeId): void {
  ambientBiome = biome;
  const profile = ambientProfileForBiome(biome);
  if (!profile.frogs) nearLake = false;
  if (windGain && ctx) windGain.gain.setTargetAtTime(profile.wind, ctx.currentTime, 0.45);
}

/** Read-only diagnostics used by the real-land browser profile. */
export function ambientState(): { biome: BiomeId; profile: AmbientProfile } {
  return { biome: ambientBiome, profile: { ...ambientProfileForBiome(ambientBiome) } };
}

let windStarted = false;
let owlTimer: ReturnType<typeof setTimeout> | null = null;

export function startAmbience(): void {
  if (windStarted) return;
  windStarted = true;
  const c = getCtx();
  if (!sfxBus) return;
  // Wind: looped noise through a slow-wandering lowpass.
  const len = c.sampleRate * 4;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    // Brown-ish noise integrates white noise.
    last = (last + (Math.random() * 2 - 1) * 0.02) * 0.998;
    data[i] = last * 3.5;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 320;
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.07;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 140;
  lfo.connect(lfoGain).connect(filter.frequency);
  windGain = c.createGain();
  windGain.gain.value = ambientProfileForBiome(ambientBiome).wind;
  src.connect(filter).connect(windGain).connect(sfxBus);
  src.start();
  lfo.start();
  scheduleOwl();
  scheduleCrickets();
}

function scheduleOwl(): void {
  if (owlTimer) clearTimeout(owlTimer);
  owlTimer = setTimeout(() => {
    if (!settings.muted && ctx && ambientProfileForBiome(ambientBiome).owl) {
      const pan = Math.random() * 1.4 - 0.7;
      tone(392, 330, 0.35, 'sine', 0.07, 0, pan);
      tone(370, 300, 0.5, 'sine', 0.06, 0.45, pan);
    }
    scheduleOwl();
  }, 18000 + Math.random() * 22000);
}

let cricketTimer: ReturnType<typeof setTimeout> | null = null;
let nearLake = false;

/** Game flags proximity so the local pond life knows when to sing. */
export function setNearLake(v: boolean): void {
  nearLake = v && ambientProfileForBiome(ambientBiome).frogs;
}

function scheduleCrickets(): void {
  if (cricketTimer) clearTimeout(cricketTimer);
  cricketTimer = setTimeout(() => {
    const profile = ambientProfileForBiome(ambientBiome);
    if (!settings.muted && ctx && profile.insects) {
      const pan = Math.random() * 1.6 - 0.8;
      const f = 3800 + Math.random() * 900;
      for (let i = 0; i < 4 + Math.floor(Math.random() * 3); i++) {
        tone(f, f * 0.97, 0.045, 'sine', 0.028, i * 0.075, pan);
      }
      // Frogs answer from the reeds when the saucer hangs over the lake.
      if (profile.frogs && nearLake && Math.random() < 0.65) {
        const fpan = Math.random() * 0.8 - 0.4;
        tone(96, 72, 0.16, 'square', 0.05, 0.5, fpan);
        tone(88, 66, 0.13, 'square', 0.045, 0.72, fpan);
      }
    }
    scheduleCrickets();
  }, 5000 + Math.random() * 9000);
}

/* ---------------- music ---------------- */
// Beds: "600 Billion Eyes" (valley), "Desert Mesa", "Snow Valley", "Moors",
// "Mars" and "Moon" (their biomes), "$600B HOLE" (Fiat Town and the wave-five
// pressure change until the next land), "Saucer Game Over" (end credits). Biome/credits tracks ship as
// 160 kbps AAC for a sane mobile loading path; the uncompressed WAV masters
// live in music-src/.

export type MusicTrack =
  | 'title' | 'eyes' | 'hole' | 'mesa' | 'snow' | 'moor' | 'mars' | 'moon' | 'gameover';

const MUSIC_SRC: Record<MusicTrack, string> = {
  title: '/music/saucer-theme.m4a',
  eyes: '/music/600-billion-eyes.m4a',
  hole: '/music/600b-hole.m4a',
  mesa: '/music/desert-mesa.m4a',
  snow: '/music/snow-valley.m4a',
  moor: '/music/moors.m4a',
  mars: '/music/mars.m4a',
  moon: '/music/moon.m4a',
  gameover: '/music/saucer-game-over.m4a',
};
const MUSIC_LEVEL = 0.45;

/** Each land's opening bed. Every land has its own except the fiat town,
 *  which gets "$600B HOLE" — written for it, and doubling as the late-wave
 *  bed everywhere. */
export const BIOME_TRACK: Record<BiomeId, MusicTrack> = {
  valley: 'eyes',
  mesa: 'mesa',
  snow: 'snow',
  moor: 'moor',
  town: 'hole',
  mars: 'mars',
  moon: 'moon',
};

const musicEls = new Map<MusicTrack, HTMLAudioElement>();
const musicWiredEls = new Set<MusicTrack>();
let currentTrack: MusicTrack | null = null;
let fadeTimer: ReturnType<typeof setInterval> | null = null;
let musicBlocked = false;

// Set only by `suppressMusic`, for a listening test that needs a quiet room.
// Never persisted, and never a substitute for the player's own music setting.
let musicSuppressed = false;

function playMusicElement(el: HTMLAudioElement): void {
  if (!settings.music || musicSuppressed) return;
  void el.play().then(() => { musicBlocked = false; }, () => { musicBlocked = true; });
}

function getMusicEl(track: MusicTrack): HTMLAudioElement {
  let el = musicEls.get(track);
  if (!el) {
    el = new Audio(MUSIC_SRC[track]);
    el.loop = true;
    el.preload = 'auto';
    el.volume = 0;
    el.muted = settings.muted || !settings.music || musicSuppressed;
    musicEls.set(track, el);
  }
  if (!musicWiredEls.has(track) && musicBus && ctx) {
    // Desktop: route through WebAudio so voice clips can duck the bed. Mobile
    // Safari streams more reliably straight through the element, so skip there.
    const coarse = matchMedia('(pointer: coarse)').matches;
    if (!coarse) {
      try {
        ctx.createMediaElementSource(el).connect(musicBus);
      } catch { /* already wired; element output is fine */ }
    }
    musicWiredEls.add(track);
  }
  return el;
}

/** Buffer a bed until the browser thinks it can play through — with a timeout
 *  so a slow connection delays the run, never blocks it. */
export function preloadMusicTrack(track: MusicTrack, timeoutMs = 8000): Promise<void> {
  const el = getMusicEl(track);
  if (el.readyState >= 4) return Promise.resolve();
  // primeMusicTrack may already have started the request from a user gesture.
  // Calling load() again aborts that authorised play promise on Chromium and,
  // more seriously, loses the per-element authorisation on WebKit.
  if (el.networkState === HTMLMediaElement.NETWORK_EMPTY) el.load();
  return new Promise((resolve) => {
    const done = (): void => {
      el.removeEventListener('canplaythrough', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    el.addEventListener('canplaythrough', done);
  });
}

/** Authorise the opening gameplay bed from the same user gesture that starts
 * the title music. WebKit grants media playback per element; without this,
 * switching elements after the async loader can be rejected as autoplay. */
export function primeMusicTrack(track: MusicTrack): void {
  const el = getMusicEl(track);
  if (!el.paused) return;
  const volume = el.volume;
  el.volume = 0;
  void el.play().then(() => {
    musicBlocked = false;
    if (currentTrack !== track) {
      el.pause();
      el.currentTime = 0;
      el.volume = volume;
    }
  }).catch(() => { musicBlocked = true; el.volume = volume; });
}

/** iOS ignores JS volume writes, so a volume crossfade can never finish there. */
function volumeFadeWorks(el: HTMLAudioElement): boolean {
  const prev = el.volume;
  const probe = prev > 0.5 ? prev - 0.3 : prev + 0.3;
  el.volume = probe;
  const works = Math.abs(el.volume - probe) < 0.01;
  el.volume = prev;
  return works;
}

export function setMusicTrack(track: MusicTrack): void {
  if (currentTrack === track) return;
  const prev = currentTrack;
  const from = prev ? getMusicEl(prev) : null;
  currentTrack = track;
  const to = getMusicEl(track);
  // A switch during an unfinished fade must not orphan a third playing bed.
  for (const [key, el] of musicEls) {
    if (key !== track && key !== prev) el.pause();
  }
  if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
  if (!settings.music) {
    from?.pause();
    to.volume = MUSIC_LEVEL;
    to.muted = true;
    return;
  }
  to.muted = settings.muted;
  playMusicElement(to);
  if (from && !volumeFadeWorks(from)) {
    // Mobile Safari: hard swap — the fade loop used to leave both songs playing.
    from.pause();
    to.volume = MUSIC_LEVEL;
    return;
  }
  let ticks = 0;
  fadeTimer = setInterval(() => {
    ticks++;
    let done = true;
    if (from && !from.paused) {
      from.volume = Math.max(0, from.volume - 0.06);
      if (from.volume <= 0.02 || ticks > 24) from.pause();
      else done = false;
    }
    if (to.volume < MUSIC_LEVEL - 0.02 && ticks <= 24) {
      to.volume = Math.min(MUSIC_LEVEL, to.volume + 0.06);
      done = false;
    }
    if (done && fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
  }, 90);
}

export function startMusic(): void {
  getCtx();
  setMusicTrack(currentTrack ?? 'eyes');
  const el = currentTrack ? getMusicEl(currentTrack) : null;
  if (el) playMusicElement(el);
}

/** Retry a rejected/paused bed from any later genuine user gesture. Browsers
 * can revoke media permission after an async gate even though SFX WebAudio is
 * already running; the previous silent catch gave the pilot no recovery. */
export function nudgeMusic(): void {
  if (settings.muted || !settings.music || musicSuppressed || !currentTrack) return;
  const el = getMusicEl(currentTrack);
  if (el.paused || musicBlocked) playMusicElement(el);
}

export function stopMusic(): void {
  for (const el of musicEls.values()) el.pause();
}

/**
 * Silence the bed for the duration of something that needs a quiet room — a
 * listening test — without touching what the player chose.
 *
 * Deliberately not `setAudioMix`: that persists, so a tool that borrowed the
 * mix and was closed by navigating away rather than by its own close button
 * would leave the player's music switched off for good. It did exactly that
 * once. Nothing here is written to storage.
 */
export function suppressMusic(quiet: boolean): void {
  musicSuppressed = quiet;
  for (const el of musicEls.values()) {
    el.muted = settings.muted || !settings.music || quiet;
    if (quiet) el.pause();
  }
  if (!quiet) nudgeMusic();
}

export function suspendAudio(): void {
  setBeamHum(false);
  stopRotorField();
  setHeliRotor(false);
  setGridHum(0);
  for (const el of musicEls.values()) el.pause();
  if (ctx && ctx.state === 'running') void ctx.suspend().catch(() => undefined);
}

export function resumeAudio(resumeTrack: boolean): void {
  if (ctx && unlocked) void ctx.resume().catch(() => undefined);
  if (resumeTrack) nudgeMusic();
}
